// Free browser voice for Edgar: speech-to-text, text-to-speech and the "Hey Edgar" wake word.
const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
export const voiceSupported = !!SR;
const lang = () => navigator.language || "en-US";

const VKEY = "snaplist.edgarVoiceName";
const MALE = /\b(daniel|alex|aaron|evan|fred|tom|arthur|oliver|reed|rocko|ralph|gordon|lee|rishi|guy|christopher|eric|roger|steffan|andrew|brian|davis|tony|jason|ryan|thomas|william|james|nathan|liam|noah|george|male)\b/i;
const FEMALE = /\b(female|samantha|victoria|karen|moira|tessa|fiona|susan|zira|aria|jenny|michelle|ava|allison|serena|kate|nicky|zoe|joanna|emma|libby|sonia|natasha|catherine|hazel|heera|veena|kathy|princess|grandma|shelley|sandy|flo)\b/i;
const score = (v) => (/natural|neural|enhanced|premium|online/i.test(v.name) ? 6 : 0) + (MALE.test(v.name) ? 5 : 0)
  + (/^en/i.test(v.lang) ? 3 : 0) + (/^en-US/i.test(v.lang) ? 1 : 0) - (/novelty|whisper|bells|bubbles|zarvox|trinoids|cellos|organ|bad news|good news|jester|superstar|wobble|boing/i.test(v.name) ? 20 : 0);

// Male English voices this device has, most natural first (falls back to any English voice).
export function maleVoices() {
  const all = window.speechSynthesis?.getVoices() || [];
  const en = all.filter((v) => /^en/i.test(v.lang) && score(v) > -5);
  const men = en.filter((v) => !FEMALE.test(v.name) && MALE.test(v.name));
  return (men.length ? men : en).sort((a, b) => score(b) - score(a));
}

export const savedVoiceName = () => localStorage.getItem(VKEY) || "";
export const saveVoiceName = (n) => (n ? localStorage.setItem(VKEY, n) : localStorage.removeItem(VKEY));
const pickVoice = () => { const list = maleVoices(); return list.find((v) => v.name === savedVoiceName()) || list[0] || null; };

const rate = () => { try { return Number(JSON.parse(localStorage.getItem("snaplist.edgarVoice"))?.rate) || 1; } catch { return 1; } };

export const AI_VOICES = [["onyx", "Onyx · deep"], ["echo", "Echo · smooth"], ["ash", "Ash · clear"], ["fable", "Fable · expressive"], ["sage", "Sage · measured"], ["alloy", "Alloy · balanced"]];
const aiVoice = () => { const n = savedVoiceName() || "ai:onyx"; return n.startsWith("ai:") ? n.slice(3) : null; };

// One reusable audio element, unlocked on the first tap so phones allow later plays.
let player = null;
let playId = 0;
const getPlayer = () => { player = player || new Audio(); return player; };
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", () => { const p = getPlayer(); if (!p.src) { p.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA"; p.play().catch(() => {}); } }, { once: true, capture: true });
}

function deviceSpeak(text, onDone) {
  if (!window.speechSynthesis) { onDone?.(); return; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.replace(/…/g, ""));
  const v = pickVoice();
  if (v) { u.voice = v; u.lang = v.lang; } else u.lang = lang();
  u.rate = rate();
  u.pitch = v ? 1 : 0.85;
  u.onend = () => onDone?.();
  u.onerror = () => onDone?.();
  window.speechSynthesis.speak(u);
}

// Natural AI voice when picked (capped server-side); falls back to the device voice on any problem.
export function speak(text, onDone) {
  const voice = aiVoice();
  hush();
  if (!voice) { deviceSpeak(text, onDone); return; }
  const id = ++playId;
  fetchAudio(text).then((url) => {
    if (id !== playId) return;
    if (!url) { deviceSpeak(text, onDone); return; }
    const p = getPlayer();
    p.src = url;
    p.onended = () => onDone?.();
    p.onerror = () => onDone?.();
    p.play().catch(() => deviceSpeak(text, onDone));
  });
}

export const hush = () => { playId++; window.speechSynthesis?.cancel(); if (player && !player.paused) { player.pause(); player.onended = null; } };

const fetchAudio = (text) => {
  const voice = aiVoice();
  if (!voice) return Promise.resolve(null);
  return import("@/lib/api").then(({ api }) => api.post("/edgar/speak", { text: text.replace(/…/g, "").slice(0, 900), voice, speed: Math.min(2, Math.max(0.5, rate())) }, { responseType: "blob", timeout: 20000 }))
    .then(({ data }) => URL.createObjectURL(data)).catch(() => null);
};

// Speak a streamed answer piece by piece: audio for the next piece is fetched while the current one plays.
export function speechQueue(onDone) {
  const q = [];
  let busy = false;
  let finished = false;
  const myId = ++playId;
  const next = async () => {
    if (myId !== playId) return;
    if (!q.length) { busy = false; if (finished) onDone?.(); return; }
    busy = true;
    const { text, audio } = q.shift();
    const url = await audio;
    if (myId !== playId) return;
    const after = () => { if (myId === playId) next(); };
    if (!url) { deviceSpeak(text, after); return; }
    const p = getPlayer();
    p.src = url;
    p.onended = after;
    p.onerror = after;
    p.play().catch(() => deviceSpeak(text, after));
  };
  return {
    add(text) { const t = text.trim(); if (!t) return; q.push({ text: t, audio: fetchAudio(t) }); if (!busy) next(); },
    end() { finished = true; if (!busy && !q.length && myId === playId) onDone?.(); },
  };
}

export const busy = { on: false };

export function listenOnce(onText, onEnd) {
  busy.on = true;
  try { busy.stopWake?.(); } catch { /* noop */ }
  const r = new SR();
  r.lang = lang();
  r.interimResults = false;
  r.maxAlternatives = 1;
  let heard = false;
  r.onresult = (e) => { heard = true; onText(e.results[0][0].transcript); };
  r.onerror = () => {};
  r.onend = () => { busy.on = false; onEnd?.(heard); };
  try { r.start(); } catch { busy.on = false; onEnd?.(false); }
  return r;
}

const WAKE = /\b(hey|hi|hello|ok|okay)[ ,]+(edgar|edger|edga|edgr)\b/;

// Listens in the background until it hears "Hey Edgar"; returns a stop function.
export function wakeListener(onWake, onDenied) {
  let stopped = false;
  let r;
  const start = () => {
    if (stopped) return;
    r = new SR();
    r.lang = lang();
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (WAKE.test(e.results[i][0].transcript.toLowerCase())) { stopped = true; r.stop(); onWake(); return; }
      }
    };
    r.onerror = (ev) => { if (ev.error === "not-allowed" || ev.error === "service-not-allowed") { stopped = true; onDenied?.(); } };
    r.onend = () => { if (!stopped) setTimeout(start, busy.on ? 1500 : 500); };
    if (busy.on) { setTimeout(start, 1000); return; }
    try { r.start(); busy.stopWake = () => r.stop(); } catch { /* already running */ }
  };
  start();
  return () => { stopped = true; try { r?.stop(); } catch { /* noop */ } };
}

// Spoken commands; anything else is a question.
export function command(text) {
  const t = text.toLowerCase();
  if (/\b(headphones? mode|pocket mode)\b/.test(t)) return /\b(off|stop|exit|end|turn off)\b/.test(t) ? "pocket-off" : "pocket";
  if (/\b(how am i doing|hows it going|how is it going|my total|session total|how many (have i|did i) kept?|whats my total)\b/.test(t.replace(/'/g, ""))) return "status";
  if (askPrice(t) != null) return "price";
  if (/\b(keep it|keep this|keep that|keeper|thumbs up)\b/.test(t)) return "keep";
  if (/\b(skip it|skip this|skip that|pass on it|next one)\b/.test(t)) return "skip";
  if (/\b(what('?s| is) (this|it) worth|research (this|it)|price (this|it)|how much is (this|it)|check (this|it))\b/.test(t)) return "worth";
  if (/\bscan (a |the |this )?barcode\b/.test(t)) return "scan";
  if (/^(next|next step|continue|ok next|okay next)\b/.test(t)) return "next";
  if (/^(back|go back|previous)\b/.test(t)) return "back";
  if (/\b(end tour|stop tour|end walkthrough|im done|i'm done)\b/.test(t)) return "end";
  if (/\b(take me there|go there|open it)\b/.test(t) || /^(show me|take me|show me that|show me how)$/.test(t)) return "go";
  if (/^(stop|stop listening|be quiet|quiet|cancel|nevermind|never mind)\b/.test(t)) return "stop";
  if (/\bnew chat\b|\bstart over\b/.test(t)) return "reset";
  if (/^(close|goodbye|bye|close edgar)\b/.test(t)) return "close";
  return null;
}

const money = (n) => (n == null || n === "" || Number.isNaN(Number(n)) ? null : `$${Math.round(Number(n))}`);

// "they want 20 dollars" / "asking $15" / "it's 8 bucks" → 20 / 15 / 8.
export function askPrice(text) {
  const t = text.toLowerCase().replace(/,/g, "");
  const m = t.match(/\$\s?(\d+(?:\.\d+)?)/) || t.match(/(\d+(?:\.\d+)?)\s*(?:dollars?|bucks)\b/)
    || t.match(/\b(?:they want|they're asking|theyre asking|asking|price is|priced at|it's|its|costs?)\s+(\d+(?:\.\d+)?)\b/);
  return m ? Number(m[1]) : null;
}

// Buy or pass on the last result at the asking price, using the user's flip target.
export function priceSpeech(fm) {
  const ask = money(fm.buy);
  const max = fm.maxBuy != null ? money(fm.maxBuy) : null;
  const buy = fm.maxBuy != null ? fm.buy <= fm.maxBuy + 0.004 : fm.profit > 0;
  const gain = fm.profit >= 0 ? `you'd make about ${money(fm.profit)}${fm.realRoi != null ? `, a ${Math.round(fm.realRoi)} percent return` : ""}` : `you'd lose about ${money(-fm.profit)}`;
  return `${buy ? "Buy it." : "Pass."} At ${ask} ${gain}.${max ? ` The most I'd pay is ${max}.` : ""}`;
}

export function statusSpeech(k) {
  const parts = [k.count ? `You've kept ${k.count} item${k.count === 1 ? "" : "s"} today, worth about ${money(k.value) || "$0"}.` : "You haven't kept anything yet today."];
  if (k.goal) parts.push(k.count >= k.goal ? `You hit your goal of ${k.goal}. Nice!` : `${k.goal - k.count} more to hit your goal of ${k.goal}.`);
  if (k.streak > 1) parts.push(`You're on a ${k.streak} day streak.`);
  return parts.join(" ");
}

// What Edgar says when a research result comes back.
export function resultSpeech(run, canKeep) {
  const s = run.stats || {};
  const price = money(run.suggested_price);
  const lo = money(s.low);
  const hi = money(s.high);
  const parts = [run.title ? `${run.title.split(/[,|–-]/)[0].trim()}.` : "Got it."];
  if (price) parts.push(`List it around ${price}.`);
  if (lo && hi && lo !== hi) parts.push(`Comps run ${lo} to ${hi}.`);
  if (run.demand_level) parts.push(`${run.demand_level} demand${run.est_days_to_sell ? `, sells in about ${Math.round(run.est_days_to_sell)} days` : ""}.`);
  if (!price && !lo) parts.push("I couldn't find good comps for this one.");
  if (canKeep) parts.push("Say keep it, or skip it.");
  return parts.join(" ");
}
